/* Shared existing builder presentation for IO and native controls; no selection ownership. */
(function(root){
'use strict';
const bmPillChannels = value => {
  if (!value || !/^rgba?\(/.test(value)) return null;
  const parts = value.match(/[\d.]+/g)?.map(Number);
  return parts?.length >= 3 ? [parts[0], parts[1], parts[2], parts[3] ?? 1] : null;
};
const bmPillLuminance = rgb => rgb.map(channel => {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}).reduce((sum, value, index) => sum + value * [0.2126, 0.7152, 0.0722][index], 0);
function paintBmRatioPill(pill, rgb, fraction, index) {
  if (!rgb) {
    for (const property of ['background-color', 'background-image', 'color']) {
      if (pill.style.getPropertyValue(property)) pill.style.removeProperty(property);
    }
    return;
  }
  // Use the existing authoritative scent color at a consistent soft tint.
  // The filled length shows the ratio; the paired pills fill from opposite edges.
  const tintAlpha = 0.5, percent = Math.max(0, Math.min(100, Math.round(fraction * 100)));
  const layers = [];
  for (let node = pill.parentElement; node; node = node.parentElement) {
    const layer = bmPillChannels(getComputedStyle(node).backgroundColor);
    if (layer) layers.push(layer);
    if (layer?.[3] === 1) break;
  }
  let backdrop = [255, 255, 255];
  layers.reverse().forEach(layer => {
    backdrop = backdrop.map((channel, i) => layer[i] * layer[3] + channel * (1 - layer[3]));
  });
  const tinted = rgb.map((channel, i) => channel * tintAlpha + backdrop[i] * (1 - tintAlpha));
  const contrast = (ink, background) => {
    const a = bmPillLuminance(ink), b = bmPillLuminance(background);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  };
  const normalInk = [63, 59, 54];
  const ink = Math.min(contrast(normalInk, tinted), contrast(normalInk, backdrop)) >= 4.5 ? normalInk : [0, 0, 0];
  const tint = 'rgba(' + [...rgb, tintAlpha].join(', ') + ')';
  const fill = 'linear-gradient(to ' + (index === 1 ? 'left' : 'right') + ', ' + tint + ' 0%, ' + tint + ' ' + percent + '%, transparent ' + percent + '%, transparent 100%)';
  const text = 'rgb(' + ink.join(', ') + ')';
  if (pill.style.backgroundColor !== 'transparent') pill.style.backgroundColor = 'transparent';
  if (pill.style.backgroundImage !== fill) pill.style.backgroundImage = fill;
  if (pill.style.color !== text) pill.style.color = text;
}
function ensureBlendNote(row17d) {
  if (!row17d) return;

  if (row17d.querySelector('.bm-blend-note-wrap')) return;

  var wrap = document.createElement('div');
  wrap.className = 'bm-blend-note-wrap';

  var helper = document.createElement('div');
  helper.className = 'bm-mix2-helper';
  helper.textContent = 'Second percentage auto-fills based on your first selection.';

  var note = document.createElement('div');
  note.className = 'bm-blend-note-inline';
  note.innerHTML = `
    Each blend is mixed to order using oils with different strengths.
    Percentages reflect the blend, not scent intensity. Shake before use.
    <em>All blended oils are final sale.</em>
  `;

  wrap.appendChild(helper);
  wrap.appendChild(note);
  row17d.appendChild(wrap);
}
function mount(context, a, b, c, d, s1, p1, s2, p2) {
  const {scope:ioScope,canon,selectedText,isPlaceholder,images:MAP,defaultSwatch:DEFAULT_SWATCH,colors:bmSignatureColors}=context;
  const bmSignatureKey=canon;
  if (!a || !b || !c || !d || !s1 || !p1 || !s2 || !p2) return;
  if (!document.querySelector('.bm-build-disclosure')) return;
  ensureBlendNote(d);
  let panel = ioScope.querySelector('.bm-builder-panel');
  if (!panel) {
    panel = document.createElement('div');
    panel.className = 'bm-builder-panel';
    a.parentNode.insertBefore(panel, a);
  }
  const disclosure = document.querySelector('.bm-build-disclosure');
  if (disclosure && disclosure.parentNode !== panel) panel.prepend(disclosure);
  const toggle = disclosure.querySelector('.bm-build-toggle');
  let closeTip = disclosure.querySelector('.bm-builder-close-tip');
  if (!closeTip) {
    closeTip = document.createElement('span');
    closeTip.className = 'bm-builder-close-tip';
    closeTip.id = 'bm-builder-close-tip';
    closeTip.setAttribute('role', 'tooltip');
    closeTip.textContent = 'Closing keeps your selections. Reopen anytime on this page.';
    toggle.after(closeTip);
  }
  toggle._bmRefreshLabel = () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    const label = panel.dataset.bmProgress === 'complete' ? 'EDIT YOUR BLEND' : panel.dataset.bmProgress === 'started' ? 'CONTINUE YOUR BLEND' : 'CREATE SOMETHING UNIQUELY YOURS';
    toggle.setAttribute('aria-label', open ? 'Close scent builder' : label);
    if (open) toggle.setAttribute('aria-describedby', closeTip.id);
    else toggle.removeAttribute('aria-describedby');
    if (!open && toggle.textContent !== label) toggle.textContent = label;
  };
  if (!toggle.dataset.bmAccessibleClose) {
    toggle.dataset.bmAccessibleClose = '1';
    new MutationObserver(() => toggle._bmRefreshLabel()).observe(toggle, {attributes:true, attributeFilter:['aria-expanded']});
  }
  [a,b,c,d].forEach(row => { if (row.parentNode !== panel) panel.appendChild(row); });

  let ratios = panel.querySelector('.bm-ratios');
  if (!ratios) {
    ratios = document.createElement('fieldset');
    ratios.className = 'bm-ratios';
    ratios.innerHTML = '<legend>Blend ratio · Scent 1 / Scent 2</legend><div class="bm-ratio-buttons"></div><p>Scent 2 percentage adjusts automatically.</p>';
    [25,50,75].forEach(percent => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.percent = percent;
      button.textContent = percent + '% / ' + (100-percent) + '%';
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => {
        const field = panel.querySelector('.labeladdon-17b select');
        const option = Array.from(field.options).find(o => canon(o.textContent) === percent + '%');
        if (!option || option.disabled || field.disabled) return;
        field.dispatchEvent(new Event('focus'));
        field.value = option.value;
        field.dispatchEvent(new Event('change', {bubbles:true}));
      });
      ratios.querySelector('.bm-ratio-buttons').appendChild(button);
    });
    panel.insertBefore(ratios, d);
  }
  let summary = panel.querySelector('.bm-your-blend');
  if (!summary) {
    summary = document.createElement('div');
    summary.className = 'bm-your-blend';
    summary.innerHTML = '<h3><span>Your blend</span></h3><div class="bm-blend-preview"><div class="bm-preview-scent"><img alt=""><strong></strong><span class="bm-preview-name"></span></div><span class="bm-preview-plus" aria-hidden="true">+</span><div class="bm-preview-scent"><img alt=""><strong></strong><span class="bm-preview-name"></span></div></div><p class="bm-blend-signature">a blend all your own</p><p class="bm-preview-status" role="status" aria-live="polite"></p>';
    panel.insertBefore(summary, d);
  }
  // Restore only our component order if the app replaces one of its rows.
  summary.querySelector('.bm-blend-signature .bm-blend-heart')?.remove();
  if (!summary.querySelector('.bm-preview-plus img')) {
    const heart = document.createElement('img');
    heart.className = 'bm-blend-heart';
    heart.alt = '';
    heart.src = 'https://cdn.shopify.com/s/files/1/2554/8714/files/IMG_3327.png?v=1746970460';
    const plus = document.createElement('span');
    plus.textContent = '+';
    summary.querySelector('.bm-preview-plus').replaceChildren(plus, heart);
  }
  // Check first to avoid feeding the existing IO MutationObservers a move loop.
  const ordered = [disclosure,a,c,b,ratios,summary,d].filter(Boolean);
  ordered.forEach((node,index) => {
    const current = Array.from(panel.children).filter(child => ordered.includes(child))[index];
    if (current !== node) panel.insertBefore(node, current || null);
  });
  // Hide only the old percentage controls; retain the full original advisory.
  b.classList.add('bm-percent-source');
  d.classList.add('bm-percent-source');
  [a,c].forEach(row => {
    row.querySelector('.bm-scent-name')?.remove();
    const field = row.querySelector('select');
    if (field.closest('.bm-scent-control')) return;
    const control = document.createElement('div');
    control.className = 'bm-scent-control';
    field.parentNode.insertBefore(control, field);
    const name = document.createElement('span');
    name.className = 'bm-select-display';
    name.setAttribute('aria-hidden', 'true');
    control.append(name, field);
  });
  function paintBuilder() {
    // Read current fields: Infinite Options can replace a single row after mounting.
    s1 = panel.querySelector('.labeladdon-17a select');
    p1 = panel.querySelector('.labeladdon-17b select');
    s2 = panel.querySelector('.labeladdon-17c select');
    p2 = panel.querySelector('.labeladdon-17d select');
    if (!s1 || !p1 || !s2 || !p2) return;
    const first = parseInt(selectedText(p1),10);
    ratios.querySelectorAll('button').forEach(button => {
      const option = Array.from(p1.options).find(o => canon(o.textContent) === button.dataset.percent + '%');
      button.disabled = !option || option.disabled || p1.disabled;
      button.setAttribute('aria-pressed', String(first === Number(button.dataset.percent)));
    });
    const parts = [[s1,p1],[s2,p2]];
    const started = parts.some(([s]) => !isPlaceholder(selectedText(s)));
    [s1,s2].forEach(field => {
      const label = selectedText(field);
      const caption = field.closest('.bm-scent-control')?.querySelector('.bm-select-display');
      const text = !isPlaceholder(label) ? label : 'Choose a scent';
      if (caption) {
        caption.dataset.placeholder = String(isPlaceholder(label));
        if (caption.textContent !== text) caption.textContent = text;
      }
    });
    summary.querySelectorAll('.bm-preview-scent').forEach((item,index) => {
      const name = selectedText(parts[index][0]);
      const chosen = !isPlaceholder(name);
      const img = item.querySelector('img');
      const src = chosen ? (MAP[canon(name)] || DEFAULT_SWATCH) : DEFAULT_SWATCH;
      if (img.getAttribute('src') !== src) img.src = src;
      const label = chosen ? name : started ? 'Choose your scent' : '';
      const percent = parseInt(selectedText(parts[index][1]),10);
      const hasRatio = [25,50,75].includes(percent);
      const pct = hasRatio ? percent + '%' : started ? 'Choose your ratio' : '';
      const nameEl = item.querySelector('.bm-preview-name');
      if (nameEl.textContent !== label) nameEl.textContent = label;
      nameEl.hidden = !started;
      const pctEl = item.querySelector('strong');
      pctEl.dataset.placeholder = String(!hasRatio);
      if (pctEl.textContent !== pct) pctEl.textContent = pct;
      pctEl.hidden = !started && !hasRatio;
      paintBmRatioPill(pctEl, chosen && hasRatio ? bmSignatureColors.get(bmSignatureKey(name)) : null, percent / 100, index);
      let dots = item.querySelector('.bm-proportion-dots');
      if (!dots) {
        dots = document.createElement('span');
        dots.className = 'bm-proportion-dots';
        dots.setAttribute('aria-hidden','true');
        img.after(dots);
      }
      const count = chosen && [25,50,75].includes(percent) ? percent / 25 : 0;
      const accent = chosen && window.PR_BM_SCENT_ACCENT ? window.PR_BM_SCENT_ACCENT(name) : '';
      if (dots.style.getPropertyValue('--bm-scent-accent') !== accent) dots.style.setProperty('--bm-scent-accent',accent);
      if (dots.childElementCount !== count) dots.replaceChildren(...Array.from({length:count},() => document.createElement('i')));
      dots.hidden = count === 0;
      const row = parts[index][0].closest(index === 0 ? '.labeladdon-17a' : '.labeladdon-17c');
      let selectorDots = row.querySelector('.bm-selector-dots');
      if (!selectorDots) {
        selectorDots = document.createElement('span');
        selectorDots.className = 'bm-proportion-dots bm-selector-dots';
        selectorDots.setAttribute('aria-hidden','true');
        row.appendChild(selectorDots);
      }
      if (selectorDots.style.getPropertyValue('--bm-scent-accent') !== accent) selectorDots.style.setProperty('--bm-scent-accent',accent);
      if (selectorDots.childElementCount !== count) selectorDots.replaceChildren(...Array.from({length:count},() => document.createElement('i')));
    });
    const complete = canon(selectedText(s1)) !== canon(selectedText(s2)) && first + parseInt(selectedText(p2),10) === 100 && parts.every(([s,p]) => !isPlaceholder(selectedText(s)) && [25,50,75].includes(parseInt(selectedText(p),10)));
    summary.dataset.complete = String(complete);
    const bothScents = parts.every(([s]) => !isPlaceholder(selectedText(s)));
    const center = summary.querySelector('.bm-preview-plus');
    center.querySelector('span').hidden = bothScents;
    center.querySelector('img').hidden = !bothScents;
    panel.dataset.bmProgress = complete ? 'complete' : started ? 'started' : 'empty';
    toggle._bmRefreshLabel();
    const status = summary.querySelector('.bm-preview-status');
    status.hidden = started;
    const message = complete ? selectedText(s1) + ' ' + selectedText(p1) + ' + ' + selectedText(s2) + ' ' + selectedText(p2) : 'Choose two different scents and a blend ratio to complete your blend.';
    if (status.textContent !== message) status.textContent = message;
  }
  [s1,p1,s2,p2].forEach(field => {
    if (field.dataset.bmPresentationBound) return;
    field.dataset.bmPresentationBound = '1';
    field.addEventListener('change', () => paintBuilder());
  });
  // WM-08: native model transitions update field values without DOM change events.
  // Repaint from those authoritative fields so curated selection clears BYO presentation.
  if (!panel.dataset.bmNativeStateBound) {
    panel.dataset.bmNativeStateBound = '1';
    ioScope.addEventListener('pr:blend-change', paintBuilder);
  }
  if (!panel.dataset.bmAccentBound) {
    panel.dataset.bmAccentBound = '1';
    document.addEventListener('bm-scent-accent-ready', paintBuilder);
  }
  paintBuilder();
}

root.PRBlendBuilderPresentation=Object.freeze({mount,ensureBlendNote,channels:bmPillChannels});
})(typeof globalThis!=='undefined'?globalThis:this);
